<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Col, Row, Text, Label } from '@Shelly/shelly-design-system';
	import BenefitsContent from './BenefitsContent.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isRepoUser } from 'src/helpers/user_server_conditions';

	type benefitsListType = {
		text: string;
		info: string;
		basicSubscription: {
			details?: string;
			detailsValue?: number;
			icon?: string;
		};
		premiumSubscription: {
			details?: string;
			detailsValue?: number;
			icon?: string;
		};
		betaFlag?: boolean;
		comingSoon?: boolean;
		new?: boolean;
		isAI?: boolean;
		isNotPremiumOnly?: boolean;
	};

	const benefitsList: benefitsListType[] = [
		isRepoUser() && {
			text: $format('text_premium_benefits_camera_cloud_storage', {
				default: 'Camera Cloud Storage',
			}),
			info: $format('info_premium_benefits_camera_cloud_storage', {
				default:
					"You'll be granted with 1 free camera cloud storage slot to store your camera recordings.",
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				details: $format('label_camera_slot', {
					default: 'camera slot',
				}),
				detailsValue: 1,
			},
			comingSoon: false,
			new: true,
		},
		{
			text: $format('ai_chat_title', {
				default: 'Shelly Assistant',
			}),
			info: $format('text_premium_benefits_shelly_assistant_subheader', {
				default: "Ask about your devices' status and set up scenes effortlessly.",
			}),
			basicSubscription: {
				details: $format('prompts_per_day', {
					default: 'prompts / day',
				}),
				detailsValue: 10,
			},
			premiumSubscription: {
				details: $format('prompts_per_day', {
					default: 'prompts / day',
				}),
				detailsValue: 50,
			},
			comingSoon: false,
			isAI: true,
			new: true,
		},
		{
			text: $format('text_premium_benefits_failure_detection_header', {
				default: 'AI models for device failure detection',
			}),
			info: $format('text_premium_benefits_failure_detection_subheader', {
				default:
					'Get notified in case of abnormal device behaviour based on energy consumption.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				details: $format('ai_models', {
					default: 'AI Models',
				}),
				detailsValue: 10,
			},
			comingSoon: false,
			isAI: true,
		},
		{
			text: $format('text_premium_benefits_offline_device_notification_header', {
				default: 'Offline device notification',
			}),
			info: $format('text_premium_benefits_offline_device_notification_subheader', {
				default: 'Receive notifications in case of offline Shelly device.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			comingSoon: false,
			isAI: false,
		},
		{
			text: $format('text_premium_benefits_virtual_devices_header', {
				default: 'Virtual devices',
			}),
			info: $format('text_premium_benefits_virtual_devices_subheader', {
				default: 'Extract your virtual components into virtual devices',
			}),
			basicSubscription: {
				details: $format('text_premium_benefits_virtual_device_header', {
					default: 'virtual device',
				}),
				detailsValue: 1,
			},
			premiumSubscription: {
				details: $format('text_premium_benefits_virtual_devices_header', {
					default: 'virtual devices',
				}),
				detailsValue: 20,
			},
			comingSoon: false,
			isAI: false,
			isNotPremiumOnly: true,
		},
		{
			text: $format('text_premium_benefit_history_event_log', {
				default: 'Extended History Event Log',
			}),
			info: $format('text_premium_benefits_event_log_subheader', {
				default: 'Get access to up to 100 events per device.',
			}),
			basicSubscription: {
				details: $format('text_premium_subs_basic_events_count', {
					default: 'events',
				}),
				detailsValue: 5,
			},
			premiumSubscription: {
				details: $format('text_premium_subs_premium_events_count', {
					default: 'events',
				}),
				detailsValue: 100,
			},
			betaFlag: false,
		},
		{
			text: $format('text_premium_benefit_statistics_header', {
				default: 'Extended Statistics Data Visualization',
			}),
			info: $format('text_premium_benefit_statistics_subheader', {
				default: 'Track all your data in 1-minute intervals.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: $format('text_premium_benefits_alexa_header', {
				default: 'Additional Alexa Virtual Actions',
			}),
			info: $format('text_premium_benefits_alexa_subheader', {
				default: 'Create up to 100 Alexa virtual actions.',
			}),

			basicSubscription: {
				details: $format('text_premium_benefits_basic_scenes_count', {
					default: 'scenes',
					values: { detailsValue: 3 },
				}),
				detailsValue: 3,
			},
			premiumSubscription: {
				details: $format('text_premium_benefits_basic_scenes_count', {
					default: 'scenes',
					values: { detailsValue: 100 },
				}),
				detailsValue: 100,
			},
			betaFlag: false,
		},
		{
			text: $format('text_premium_benefits_weather_widget_header', {
				default: 'Weather Information Widget',
			}),
			info: $format('text_premium_benefits_weather_widget_subheader', {
				default: 'Access detailed weather information customised to your chosen location.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: $format('text_premium_benefits_weather_scene_header', {
				default: 'Weather-forecast Scene Execution',
			}),
			info: $format('text_premium_benefits_weather_scene_subheader', {
				default: 'Create scenes that respond to the weather forecast.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: $format('text_premium_benefits_forgotten_lights_header', {
				default: 'Forgotten lights remainder',
			}),
			info: $format('text_premium_benefits_forgotten_lights_info_subheader', {
				default: 'Receive notifications in case of forgotten lights.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
		},

		{
			text: $format('text_premium_benefits_dashboards_header', {
				default: 'Additional dashboards',
			}),
			info: $format('text_premium_benefits_dashboards_subheader', {
				default: 'Create up to 20 custom dashboards.',
			}),
			basicSubscription: {
				details: $format('text_premium_benefits_basic_dashes_count', {
					default: 'dashes',
				}),
				detailsValue: 5,
			},
			premiumSubscription: {
				details: $format('text_premium_benefits_premium_dashes_count', {
					default: 'dashes',
				}),
				detailsValue: 20,
			},
		},
		{
			text: $format('text_premium_benefits_widgets_header', {
				default: 'Additional Widgets',
			}),
			info: $format('text_premium_benefits_widgets_subheader', {
				default: 'Set up to 80 custom widgets per dashboard.',
			}),
			basicSubscription: {
				details: $format('text_premium_benefits_basic_widget_count', {
					default: 'widgets',
				}),
				detailsValue: 40,
			},
			premiumSubscription: {
				details: $format('text_premium_benefits_premium_widget_count', {
					default: 'widgets',
				}),
				detailsValue: 80,
			},
		},
		{
			text: $format('text_premium_benefits_mounthy_reports_header', {
				default: 'Monthly energy reports',
			}),
			info: $format('text_premium_benefits_mounthy_reports_subheader', {
				default:
					'Get monthly energy reports within the app and have them delivered to your email.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			comingSoon: false,
		},
	].filter(Boolean);
</script>

{#each benefitsList as benefit}
	<Row>
		<Col sm="6" md="8">
			<div class="d-flex flex-direction-column">
				<div class="d-flex align-items-center flex-wrap mb-xs gap-xs">
					<Text class="text-bold" element="span" styleType="body-small">
						{capFirstChar(benefit.text)}
					</Text>
					<div class="d-flex align-items-center gap-xs">
						{#if benefit.betaFlag}
							<Label type="secondary" value="BETA" size="s" />
						{:else if benefit.comingSoon}
							<Label
								type="secondary"
								value={$format('label_premium_coming_soon', {
									default: 'Coming Soon',
								})}
								size="s"
							/>
						{/if}
						{#if benefit.isAI}
							<Label
								size="xs"
								type="ai"
								value={$format('label_ai', { default: 'ai' }).toUpperCase()}
								icon="fak fa-solid-sparkles"
								iconColor="#0a0a0a"
							/>
						{/if}
						{#if benefit.new}
							<Label
								{...!benefit.isNotPremiumOnly ? { icon: 'fa-solid fa-gem' } : {}}
								type={benefit.isNotPremiumOnly ? 'secondary' : 'premium'}
								size="xs"
								value={$format('label_premium_new', {
									default: 'New',
								}).toUpperCase()}
							/>
						{/if}
					</div>
				</div>

				<Text class="text-color-muted" styleType="body-extra-small">
					{benefit.info}
				</Text>
			</div>
		</Col>
		<Col sm="3" md="2">
			<BenefitsContent benefitSubscription={benefit.basicSubscription} />
		</Col>
		<Col sm="3" md="2">
			<BenefitsContent benefitSubscription={benefit.premiumSubscription} isPremium />
		</Col>
	</Row>
	<hr class="hr-color-2" />
{/each}
